import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { notification } from "antd";
import { NOTIFICATION_MESSAGES } from "@shared/utils";
import { getActsRegistry } from "../get-acts-registry";
import { mapDtoToRow } from "../mappers";
import { MOCK_RECORDS, MOCK_RECORDS_TOTAL } from "../mock-data";
import type { MockRecordRow } from "../mock-data";

interface UseActsRegistryParams {
  projectExternalId: string;
  page: number;
  limit: number;
  sortField?: string;
  sortDirection?: string;
}

export function useActsRegistry({ projectExternalId, page, limit, sortField, sortDirection }: UseActsRegistryParams) {
  const { data, isError, isFetching, refetch } = useQuery({
    queryKey: ["registry-of-acts", projectExternalId, page, limit, sortField, sortDirection],
    queryFn: () => getActsRegistry({ projectId: projectExternalId, pageNumber: page - 1, pageSize: limit, sortField, sortDirection }),
    enabled: Boolean(projectExternalId),
  });

  const useMocks = isError || (!isFetching && !data?.entityList?.length);

  useEffect(() => {
    if (useMocks && !isFetching) {
      notification.warning({
        message: NOTIFICATION_MESSAGES.REGISTRY_DATA_UNAVAILABLE, // "Данные реестра недоступны"
        description: isError
          ? NOTIFICATION_MESSAGES.REGISTRY_SERVER_ERROR_DESC // "Не удалось загрузить данные с сервера. Отображаются тестовые данные."
          : NOTIFICATION_MESSAGES.REGISTRY_EMPTY_LIST_DESC, // "Сервер вернул пустой список. Отображаются тестовые данные."
        placement: "topRight",
        duration: 5,
      });
    }
  }, [useMocks, isFetching, isError]);

  const total = useMocks ? MOCK_RECORDS_TOTAL : (data?.entityCount ?? 0);
  const rows: MockRecordRow[] = useMocks
    ? MOCK_RECORDS.slice((page - 1) * limit, page * limit)
    : (data?.entityList ?? []).map(mapDtoToRow);

  return { rows, total, isLoading: isFetching, refetch };
}
